import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Check, Minus } from 'lucide-react';
import { clsx } from '../clsx';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  /** Keeps the label for assistive technology only (selection column of tables). */
  labelHidden?: boolean | undefined;
  /** Second line under the label, 11.5 px muted. */
  description?: ReactNode | undefined;
  /** "Some selected" state of a select-all box. */
  indeterminate?: boolean | undefined;
}

/** 16 px checkbox of the screens: hairline box, accent fill with a white check when checked. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, labelHidden = false, description, indeterminate = false, className, ...rest },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label className={clsx(styles.label, labelHidden && styles.compact, className)}>
      <input ref={inputRef} type="checkbox" className={styles.input} {...rest} />
      <span className={styles.box} aria-hidden="true">
        {indeterminate ? <Minus size={11} strokeWidth={2.5} /> : <Check size={11} strokeWidth={2.5} />}
      </span>
      <span className={clsx(styles.text, labelHidden && styles.srOnly)}>
        <span>{label}</span>
        {description ? <span className={styles.description}>{description}</span> : null}
      </span>
    </label>
  );
});
